import React from 'react'
import ReactDOM from 'react-dom'
import { useTranslation } from 'react-i18next'
import * as Sentry from '@sentry/react'
import { isChunkLoadError } from '../../utils/lazyWithRetry'
import Fulllogo from '../../assets/svg/US-full.svg'

/*
 * The app is otherwise entirely function components, but catching a render error still
 * requires a class — there is no hook equivalent of componentDidCatch.
 *
 * Without this, a rejected lazy() import (the usual cause: a deploy removed the chunk
 * an open tab was about to request) unmounts the whole tree and leaves a blank page
 * with no way back except a manual refresh.
 */

// Split out so the fallback can use hooks; the boundary itself cannot.
function ErrorFallback({ chunkError, onReload }) {
    const { t } = useTranslation()

    return (
        <div className="fixed inset-0 z-[100001] flex flex-col justify-center items-center w-full h-screen bg-deepPurple px-6 text-center">
            <img src={Fulllogo} alt="logo" className="mb-8" />
            <div className="text-highlightColor font-bold text-2xl mb-3">{t('errorBoundaryTitle')}</div>
            <div className="text-lightBG text-base mb-8 max-w-md">{chunkError ? t('errorBoundaryOutdatedMessage') : t('errorBoundaryMessage')}</div>
            <button
                type="button"
                onClick={onReload}
                className="bg-highlightColor pointer flex items-center justify-center text-deepPurple text-base font-semibold rounded-xl h-[55px] px-8 transition-all !duration-[250ms] ease-in-out hover:bg-lightBG"
            >
                {t('errorBoundaryReload')}
            </button>
        </div>
    )
}

export default class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props)
        this.state = { error: null }
        this.handleReload = this.handleReload.bind(this)
    }

    static getDerivedStateFromError(error) {
        return { error }
    }

    componentDidCatch(error, errorInfo) {
        Sentry.captureException(error, {
            tags: {
                boundary: this.props.name || 'unknown',
                chunkError: isChunkLoadError(error),
            },
            extra: { componentStack: errorInfo?.componentStack },
        })
    }

    componentDidUpdate(prevProps) {
        // `resetKey` is the route path at the mount points below: navigating away from a
        // broken screen should give the user a working app back, not a stuck fallback.
        if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
            this.setState({ error: null })
        }
    }

    handleReload() {
        window.location.reload()
    }

    render() {
        const { error } = this.state
        if (!error) return this.props.children

        // Rendered outside #root on purpose. If the crash happened while a modal was
        // open, react-modal has left #root aria-hidden (see ReactModal.setAppElement in
        // index.js) and some modals additionally blur #home - which would make this
        // screen invisible to screen readers and blurred on screen. A portal to body
        // escapes both, and is unaffected by any ancestor transform or overflow.
        const fallback = <ErrorFallback chunkError={isChunkLoadError(error)} onReload={this.handleReload} />

        return typeof document !== 'undefined' ? ReactDOM.createPortal(fallback, document.body) : fallback
    }
}
